/**
 * Upcoming class meeting resolver — SHARED source of truth.
 *
 * Enumerates real meeting occurrences between two dates by combining the
 * canonical schedule signals every surface already uses:
 *   • class-level `schedule` (weekday + time-of-day)
 *   • per-`session` `dates` free-text (explicit ranges or lists)
 *   • class registry visibility (same filter as public Calendar page)
 *
 * Every occurrence is a REAL meeting: weekday + time-of-day + a date that
 * either falls inside a session's parsed range or matches a listed day.
 * Interest-only options, "coming soon" placeholders, hidden/archived
 * classes, and cancelled meetings never produce occurrences here.
 *
 * Used by: Operations Home "Class sessions this week" metric.
 * Intended to be reused by any future "Next Seven Days" surface so the
 * three views (Public Calendar / Next Seven Days / Operations Home) never
 * disagree.
 */
import type { ClassItem, ClassSession } from "@/data/classes";
import { parseTime, parseWeekdays, type DayName } from "./sessionSchedule";
import { fetchPublicDbClasses } from "./dbClasses";
import { classes as STATIC_CLASSES } from "@/data/classes";
import { fetchClassRegistry, slugOf } from "./classRegistry";
import { migratedSlugs } from "@/data/migrationFlags";

const DAY_INDEX: Record<DayName, number> = {
  Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
  Thursday: 4, Friday: 5, Saturday: 6,
};
const DAY_NAMES: DayName[] = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
];

const MONTHS: Record<string, number> = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
  apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6,
  aug: 7, august: 7, sep: 8, sept: 8, september: 8,
  oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
};

function iso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function dayNameOf(d: Date): DayName {
  return DAY_NAMES[d.getDay()];
}

/**
 * Parse the free-form `dates` string on a ClassSession into concrete Date
 * objects. Handles:
 *   • Explicit range with two "Mon DD" tokens ("Jul 6 – Jul 31",
 *     "Sep 4 – Dec 18, 2026") — enumerated by weekly steps on `weekdays`.
 *   • Day-list with a single month ("Jul 6, 13, 20, 27") — each day used.
 *   • Skips text with no parseable "Mon DD" token (e.g.
 *     "Fall 2026 — dates coming soon").
 */
function expandSessionDates(
  text: string | null | undefined,
  weekdays: DayName[],
  refYear: number,
): Date[] {
  if (!text) return [];
  const cleaned = text.replace(/[–—]/g, "-");
  // Tokenize left-to-right. A "Mon DD" sets the current month; subsequent
  // bare ", DD" continuations reuse that month (e.g. "Jul 6, 13, 20, 27").
  // Optional 4-digit year attaches to whichever token it follows.
  const tokens: Array<{ month: number; day: number; year: number }> = [];
  const combined = /\b([A-Za-z]{3,9})\s+(\d{1,2})(?:,\s*(\d{4}))?|,\s*(\d{1,2})(?:,\s*(\d{4}))?/g;
  let currentMonth: number | null = null;
  let lastYear = refYear;
  let m: RegExpExecArray | null;
  while ((m = combined.exec(cleaned)) !== null) {
    if (m[1]) {
      const mo = MONTHS[m[1].toLowerCase()];
      if (mo === undefined) continue;
      currentMonth = mo;
      if (m[3]) lastYear = parseInt(m[3], 10);
      tokens.push({ month: mo, day: parseInt(m[2], 10), year: m[3] ? parseInt(m[3], 10) : lastYear });
    } else if (m[4] && currentMonth !== null) {
      if (m[5]) lastYear = parseInt(m[5], 10);
      tokens.push({ month: currentMonth, day: parseInt(m[4], 10), year: m[5] ? parseInt(m[5], 10) : lastYear });
    }
  }
  if (tokens.length === 0) return [];

  // Range case: exactly two named-month tokens ("Jul 6 – Jul 31", "Sep 4 – Dec 18, 2026").
  const namedCount = (cleaned.match(/\b[A-Za-z]{3,9}\s+\d{1,2}/g) ?? []).length;
  if (tokens.length === 2 && namedCount === 2) {
    const [a, b] = tokens;
    if (a.year === refYear && b.year !== refYear) a.year = b.year;
    const start = new Date(a.year, a.month, a.day);
    const end = new Date(b.year, b.month, b.day);
    if (start > end) return [];
    const wanted = new Set(weekdays.map((w) => DAY_INDEX[w]));
    const out: Date[] = [];
    const cur = new Date(start);
    while (cur <= end) {
      if (wanted.has(cur.getDay())) out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }

  // List case: honor every parsed date as-is.
  return tokens.map((t) => new Date(t.year, t.month, t.day));
}


export interface Meeting {
  classSlug: string;
  classId: string;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  weekday: DayName;
  /** "10:00 AM – 11:00 AM" or "" when unparseable */
  timeDisplay: string;
  sessionLabel: string;
}

function meetingsForClass(c: ClassItem, from: Date, to: Date): Meeting[] {
  const fallbackDays = parseWeekdays(c.schedule);
  const fallbackTime = parseTime(c.schedule);
  const sessions: ClassSession[] =
    c.sessions?.length ? c.sessions : ([] as ClassSession[]);
  const consider: ClassSession[] = sessions.length
    ? sessions
    : ([{ label: "", dates: c.schedule ?? "" } as ClassSession]);

  const out: Meeting[] = [];
  for (const s of consider) {
    // "full" sessions still meet; only truly-cancelled data should be
    // filtered upstream. If a session is explicitly marked cancelled, skip.
    if ((s as any).cancelled === true) continue;
    const days = parseWeekdays(s.dates).length
      ? parseWeekdays(s.dates)
      : fallbackDays;
    if (days.length === 0) continue;
    const t = parseTime(s.dates) ?? fallbackTime;
    const dates = expandSessionDates(s.dates, days, from.getFullYear());
    for (const d of dates) {
      if (d < from || d > to) continue;
      out.push({
        classSlug: slugOf(c),
        classId: c.id,
        title: c.title,
        date: iso(d),
        weekday: dayNameOf(d),
        timeDisplay: t?.display ?? "",
        sessionLabel: s.label ?? "",
      });
    }
  }
  return out;
}

/**
 * Enumerate every real class meeting occurring in [from, to] (inclusive).
 * Throws on data-fetch failure so callers can distinguish "genuinely empty"
 * from "query failed" (Ops Home renders "Schedule unavailable" on throw).
 */
export async function getUpcomingMeetings(
  from: Date,
  to: Date,
): Promise<Meeting[]> {
  const [dbClasses, registry] = await Promise.all([
    fetchPublicDbClasses(),
    fetchClassRegistry(),
  ]);

  const regBySlug = new Map(registry.map((r) => [r.slug, r]));
  const isCalendarVisible = (c: ClassItem) => {
    const e = regBySlug.get(slugOf(c));
    if (!e) return true; // unknown slugs default visible (matches CalendarPage)
    const status = e.visibility_status;
    const publiclyVisible =
      status === "published" || status === "coming_soon";
    return publiclyVisible && e.calendar_visible !== false;
  };

  const staticVisible = STATIC_CLASSES
    .filter((c) => !migratedSlugs.has(slugOf(c)))
    .filter(isCalendarVisible);
  const dbVisible = dbClasses.filter(isCalendarVisible);
  const visible = [...staticVisible, ...dbVisible];

  const all: Meeting[] = [];
  for (const c of visible) {
    all.push(...meetingsForClass(c, from, to));
  }
  all.sort((a, b) => a.date.localeCompare(b.date) || a.timeDisplay.localeCompare(b.timeDisplay));
  return all;
}
